<template>
	<div class="app-number-field">
		<div
			v-if="$slots.default"
			class="app-number-field__label"
		>
			<slot></slot>
		</div>

		<div
			class="app-number-field__input-wrapper"
			:class="{
				'app-number-field__input-wrapper--disabled' : $attrs.disabled,
				'app-number-field__input-wrapper--error' : hasError
			}"
		>
			<input
				type="number"
				class="app-number-field__input"
				v-bind="$attrs"
				:value="$attrs.value || 0"
				@input="inputHandler"
				@keypress="keyPressHandler"
				@keydown="keyDownHandler"
				@paste.prevent
				@invalid="setInvalidMessage"
			>
			<div class="app-number-field__ticks">
				<span class="arrow arrow--up" @click="update(1)"></span>
				<span class="arrow arrow--down" @click="update(-1)"></span>
			</div>

		</div>

		<p
			v-if="hasError"
			class="app-number-field__error"
		>
			{{ error || invalidMessage }}
		</p>
	</div>
</template>

<script>
export default {
	props: {
		error: String
	},

	data () {
		return {
			invalidMessage: null
		}
	},

	computed: {
		hasError () {
			return this.error || this.invalidMessage
		}
	},

	methods: {
		keyPressHandler (event) {
			const code = event.which || event.keyCode
			if (code === 45 &&
				Number(event.target.min) !== 0 &&
				!event.target.value
			) return

			if (code === 46 &&
				!event.target.value.includes('.') &&
				Number(event.target.value >= Number(event.target.min))
			) return

			if (code > 31 && (code > 57 || code < 48)) event.preventDefault()
		},

		keyDownHandler (event) {
			const code = event.which || event.keyCode

			if (code === 38 || code === 40) {
				event.preventDefault()
				if (code === 38) return this.tick(1)
				if (code === 40) return this.tick(-1)
			}

			return event.target.value
		},

		update (step) {
			this.tick(step)
		},

		tick (step) {
			const initial = Number(this.$attrs.value || 0)
			const update = step * Number(this.$attrs.step || 1)
			let value = initial + update
			if (this.$attrs.min) value = Math.max(value, Number(this.$attrs.min))
			if (this.$attrs.max) value = Math.min(value, Number(this.$attrs.max))
			this.invalidMessage = ''
			this.$emit('input', value)
		},

		inputHandler (event) {
			this.invalidMessage = ''
			this.$emit('input', event.target.value)
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) {
				this.invalidMessage = 'Fill up this field'
				return
			}

			if (event.target.validity.stepMismatch) return
			this.invalidMessage = 'Invalid Input'
		}
	}
}
</script>

<style lang="scss" scoped>
.app-number-field {
	display: inline-block;

	&__label, &__input {
		font-size: inherit;
		color: $default;
	}

	&__label { margin-bottom: .5em; }

	&__input-wrapper {
		width: 100%;
		display: flex;
		align-items: center;
		padding: 5px 8px;
		border: 1px solid $default-light;
		border-radius: 3px;
		color: $default;

		&:hover, &:focus-within {
			border-color: $primary;
		}

		&--error,
		&--error:hover,
		&--error:focus-within {
			border-color: $danger;
		}
	}

	&__input {
		width: 100%;
		line-height: 1em;
		font-size: 1em;
		text-align: right;
		border: none;
		outline: none;

		&::placeholder {
			color: $default-light;
			font-size: .8em;
			line-height: 1em;
		}

		/* Chrome, Safari, Edge, Opera */
		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}

		/* Firefox */
		-moz-appearance: textfield;
	}

	&__ticks {
		display: flex;
		flex-direction: column;
		position: relative;
		height: 1em;
		width: 1em;
		margin: 0 .5em;

		.arrow {
			display: inline-block;
			cursor: pointer;
			width: .4em;
			height: 0;
			border: .4em solid transparent;
			margin: 1px 0;
			position: absolute;
			left: 0;

			&--up {
				border-bottom-color: $default;
				bottom: .5em;

				&:hover {
					border-bottom-color: $primary;
				}
			}

			&--down {
				border-top-color: $default;
				top: .5em;

				&:hover {
					border-top-color: $primary;
				}
			}
		}
	}

	&__input-wrapper--disabled {
		pointer-events: none;
		opacity: 0.5;
	}

	&__error {
		color: $danger;
		margin-top: .5em;
		display: flex;
		justify-content: flex-end;
	}
}
</style>
